import type { ProgressReport, SkillProgressRow } from "@tutor/protocol";
import { Climb } from "@tutor/ui";
import { useLocale, useT } from "../i18n";
import { stageWords } from "../insights/stages";
import type { ApiError, ParentAlertRow } from "./api";
import { InsightView } from "./InsightView";
import { Sessions } from "./Sessions";
import { skillLabel } from "./skill-label";
import { parentPlural, useParentT } from "./strings";

// The Progress tab: the week in two figures, the bilan of each session (ADR-0055), every skill as the climb the child sees
// (ADR-0043) with the same stage names, then the insight cards, where parents keep the
// engine's percentages and trends. Every figure and step comes from the report.

/** Most practised this week first, then the rest by name. */
function byPractice(a: SkillProgressRow, b: SkillProgressRow): number {
  return b.attempts - a.attempts || a.skillId.localeCompare(b.skillId);
}

export function ProgressTab(props: {
  report: ProgressReport;
  alerts: readonly ParentAlertRow[];
  onAuthError: (error: ApiError) => boolean;
}) {
  const { report, alerts, onAuthError } = props;
  const t = useParentT();
  const child = useT();
  const locale = useLocale();
  const skills = [...report.skills].sort(byPractice);
  return (
    <div className="pm-progress">
      <section className="pm-week" aria-labelledby="pm-week-title">
        <h3 id="pm-week-title" className="pm-group__title">
          {t("progress.week")}
        </h3>
        <dl className="pm-tiles">
          <div className="pm-tile">
            <dt>{t("progress.exercisesLabel")}</dt>
            <dd>{new Intl.NumberFormat(locale).format(report.totals.attempts)}</dd>
          </div>
          <div className="pm-tile">
            <dt>{t("progress.daysLabel")}</dt>
            <dd>{t("progress.daysOf", { count: report.totals.activeDays, days: report.days })}</dd>
          </div>
        </dl>
      </section>

      <Sessions profileId={report.profileId} onAuthError={onAuthError} />

      <section className="pm-climbs-section" aria-labelledby="pm-climbs-title">
        <h3 id="pm-climbs-title" className="pm-group__title">
          {t("progress.climbs")}
        </h3>
        {skills.length === 0 ? (
          <p className="parent-muted">{t("progress.climbsEmpty")}</p>
        ) : (
          <ul className="pm-climbs">
            {skills.map((row) => {
              const name = skillLabel(row.skillId, locale);
              const climb = row.climb ?? null;
              const words =
                climb === null ? null : stageWords(child, name, climb.step, climb.steps);
              return (
                <li key={row.skillId} className="pm-climb" data-climb={row.skillId}>
                  <div className="pm-climb__text">
                    <span className="pm-climb__name">{name}</span>
                    <span className="pm-climb__stage">
                      {words === null ? child("insights.started") : words.where}
                    </span>
                    {row.attempts > 0 ? (
                      <span className="pm-climb__week">
                        {t("progress.thisWeek", {
                          exercises: parentPlural(locale, "progress.exercises", row.attempts),
                        })}
                      </span>
                    ) : null}
                  </div>
                  {climb === null || words === null ? null : (
                    <Climb step={climb.step} steps={climb.steps} label={words.label} />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <InsightView report={report} alerts={alerts} />
    </div>
  );
}
